import React, { useContext, useEffect, useState } from "react";
import { Container, Tab, Nav, Card, Table, Spinner, Button, Form, Modal } from "react-bootstrap";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import { AuthContext } from "../context/AuthContext";
import axios from "../context/AxiosInstance";
import { toast } from "react-toastify";
import RhTablePagination from "../components/common/RhTablePagination";
import "../styles/rh-modern-ui.css";

const useDeletedList = (endpoint) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(endpoint);
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); }, []);
  // Filtre côté client sur tous les champs
  const normalized = search.trim().toLowerCase();
  const filtered = normalized
    ? items.filter((it) => JSON.stringify(it).toLowerCase().includes(normalized))
    : items;
  const total = filtered.length;
  const view = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search]);
  return { items: view, loading, search, setSearch, refresh: fetchData, page, setPage, total, perPage, setPerPage };
};

const CorbeillePage = () => {
  const { role } = useContext(AuthContext);
  const isAdmin = (role || "").toUpperCase() === "ADMIN";

  const employes = useDeletedList("/employes/?is_active=0");
  const conges = useDeletedList("/conges/?is_active=0");
  const salaires = useDeletedList("/salaires/?is_active=0");
  const utilisateurs = useDeletedList("/utilisateurs/?deleted=1");

  const [confirm, setConfirm] = React.useState({ show: false, title: "", message: "", onConfirm: null });

  const doRestore = async (type, id) => {
    try {
      if (type === "employes") await axios.patch(`/employes/${id}/etat`, { is_active: true });
      else if (type === "conges") await axios.patch(`/conges/${id}/etat`, { is_active: true });
      else if (type === "salaires") await axios.patch(`/salaires/${id}/etat`, { is_active: true });
      else if (type === "utilisateurs") await axios.patch(`/utilisateurs/${id}/restaurer`);
      toast.success("Élément restauré avec succès");
      if (type === "employes") employes.refresh();
      if (type === "conges") conges.refresh();
      if (type === "salaires") salaires.refresh();
      if (type === "utilisateurs") utilisateurs.refresh();
    } catch (e) {
      toast.error("Échec de la restauration");
    }
  };

  const doHardDelete = async (type, id) => {
    try {
      if (type === "employes") await axios.delete(`/employes/${id}`, { params: { hard: 1 } });
      else if (type === "conges") await axios.delete(`/conges/${id}`, { params: { hard: 1 } });
      else if (type === "salaires") await axios.delete(`/salaires/${id}`, { params: { hard: 1 } });
      else if (type === "utilisateurs") await axios.delete(`/utilisateurs/${id}`, { params: { hard: 1 } });
      toast.success("Élément supprimé définitivement");
      if (type === "employes") employes.refresh();
      if (type === "conges") conges.refresh();
      if (type === "salaires") salaires.refresh();
      if (type === "utilisateurs") utilisateurs.refresh();
    } catch (e) {
      toast.error("Échec de la suppression définitive");
    }
  };

  const openConfirm = (mode, type, id, label) => {
    const isRestore = mode === "restore";
    setConfirm({
      show: true,
      title: isRestore ? "Restaurer l’élément" : "Supprimer définitivement",
      message: isRestore
        ? `Vous êtes sur le point de restaurer: ${label}. Continuer ?`
        : `Attention, cette action est irréversible. Supprimer définitivement: ${label} ?`,
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, show: false }));
        if (isRestore) await doRestore(type, id); else await doHardDelete(type, id);
      }
    });
  };

  if (!isAdmin) return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <Container fluid className="p-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Corbeille" }]} />
          <div className="alert alert-danger">Accès réservé aux administrateurs.</div>
        </Container>
      </div>
    </div>
  );

  const renderTable = (state, type) => (
    <>
      <Card className="mb-3 shadow-sm surface-card toolbar-card">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Form.Control placeholder="Recherche" value={state.search} onChange={(e) => state.setSearch(e.target.value)} className="w-50" />
            <small className="text-muted d-none d-md-inline">Résultats : {state.total}</small>
          </div>
        </Card.Body>
      </Card>
      <div className="table-responsive">
        {state.loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : (
          <Table className="modern-table table-soft">
            <thead className="table-light">
              {type === 'employes' && (
                <tr>
                  <th>#</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Matricule</th>
                  <th>Poste</th>
                  <th>Département</th>
                  <th>Téléphone</th>
                  <th>Supprimé le</th>
                  <th className="text-center">Actions</th>
                </tr>
              )}
              {type === 'conges' && (
                <tr>
                  <th>#</th>
                  <th>Employé</th>
                  <th>Année</th>
                  <th>Mois</th>
                  <th>Jours pris</th>
                  <th>Jours acquis</th>
                  <th>Supprimé le</th>
                  <th className="text-center">Actions</th>
                </tr>
              )}
              {type === 'salaires' && (
                <tr>
                  <th>#</th>
                  <th>Contrat</th>
                  <th>Mois</th>
                  <th>Année</th>
                  <th>CRG</th>
                  <th>Montant simple</th>
                  <th>Gain</th>
                  <th>Retenue</th>
                  <th>Supprimé le</th>
                  <th className="text-center">Actions</th>
                </tr>
              )}
              {type === 'utilisateurs' && (
                <tr>
                  <th>#</th>
                  <th>Nom</th>
                  <th>Prénom</th>
                  <th>Login</th>
                  <th>Rôle</th>
                  <th>Téléphone</th>
                  <th>Supprimé le</th>
                  <th className="text-center">Actions</th>
                </tr>
              )}
            </thead>
            <tbody>
              {state.items.map((it) => (
                <tr key={(type==='employes' ? (it.id_employe||it.id) : type==='conges' ? (it.id_conge||it.id) : type==='salaires' ? (it.id_salaire||it.id) : type==='utilisateurs' ? (it.id_user||it.id) : (it.id))}>
                  {type === 'employes' && (
                    <>
                      <td>{it.id_employe || it.id || '—'}</td>
                      <td>{it.nom}</td>
                      <td>{it.prenom}</td>
                      <td>{it.matricule_employe || '—'}</td>
                      <td>{it.poste || it.poste_actuel || '—'}</td>
                      <td>{it.departement || '—'}</td>
                      <td>{it.contact || it.telephone || '—'}</td>
                      <td>{it.deleted_at || '—'}</td>
                    </>
                  )}
                  {type === 'conges' && (
                    <>
                      <td>{it.id_conge || it.id || '—'}</td>
                      <td>{`${it.nom_employe || ''} ${it.prenom_employe || ''}`.trim()}</td>
                      <td>{it.annee_conge || '—'}</td>
                      <td>{it.mois_conge || '—'}</td>
                      <td>{it.jours_pris_conge || 0}</td>
                      <td>{it.jours_acquis_conge || 0}</td>
                      <td>{it.deleted_at || '—'}</td>
                    </>
                  )}
                  {type === 'salaires' && (
                    <>
                      <td>{it.id_salaire || it.id || '—'}</td>
                      <td>#{it.id_contrat}</td>
                      <td>{it.mois_salaire || it.mois || '—'}</td>
                      <td>{it.annee_salaire || it.annee || '—'}</td>
                      <td>{it.code_crg || '—'}</td>
                      <td>{it.montant_simple || 0}</td>
                      <td>{it.montant_gain_salaire || 0}</td>
                      <td>{it.montant_retenu_salaire || 0}</td>
                      <td>{it.deleted_at || '—'}</td>
                    </>
                  )}
                  {type === 'utilisateurs' && (
                    <>
                      <td>{it.id_user || it.id || '—'}</td>
                      <td>{it.nom}</td>
                      <td>{it.prenom}</td>
                      <td>{it.login || '—'}</td>
                      <td>{it.type_compte || '—'}</td>
                      <td>{it.telephone || '—'}</td>
                      <td>{it.deleted_at || '—'}</td>
                    </>
                  )}
                  <td className="text-center">
                    <Button
                      size="sm"
                      variant="outline-success"
                      className="me-2"
                      onClick={() => openConfirm("restore", type, (type==='employes' ? (it.id_employe||it.id) : type==='conges' ? (it.id_conge||it.id) : type==='salaires' ? (it.id_salaire||it.id) : type==='utilisateurs' ? (it.id_user||it.id) : (it.id)), it.nom || `${it.nom_employe||''} ${it.prenom_employe||''}`.trim() || it.libelle_crg || it.id)}
                    >
                      ♻ Restaurer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => openConfirm("delete", type, (type==='employes' ? (it.id_employe||it.id) : type==='conges' ? (it.id_conge||it.id) : type==='salaires' ? (it.id_salaire||it.id) : type==='utilisateurs' ? (it.id_user||it.id) : (it.id)), it.nom || `${it.nom_employe||''} ${it.prenom_employe||''}`.trim() || it.libelle_crg || it.id)}
                    >
                      🗑 Supprimer
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      <RhTablePagination
        currentPage={state.page}
        totalItems={state.total}
        perPage={state.perPage}
        onPageChange={state.setPage}
        onPerPageChange={(n) => { state.setPerPage(n); state.setPage(1); }}
        itemLabel="éléments"
      />
    </>
  );

  

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Navbar />
        <Container fluid className="p-5">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Corbeille" }]} />
          <h4 className="fw-bold mb-3">🗑️ Corbeille</h4>
          <div className="tabs-surface mb-4">
            <Tab.Container defaultActiveKey="employes">
              <Nav variant="pills" className="nav-pills-modern">
                <Nav.Item><Nav.Link eventKey="employes">Employés</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="conges">Congés</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="salaires">Salaires</Nav.Link></Nav.Item>
                <Nav.Item><Nav.Link eventKey="utilisateurs">Utilisateurs</Nav.Link></Nav.Item>
              </Nav>
              <Tab.Content className="mt-3">
                <Tab.Pane eventKey="employes">{renderTable(employes, "employes")}</Tab.Pane>
                <Tab.Pane eventKey="conges">{renderTable(conges, "conges")}</Tab.Pane>
                <Tab.Pane eventKey="salaires">{renderTable(salaires, "salaires")}</Tab.Pane>
                <Tab.Pane eventKey="utilisateurs">{renderTable(utilisateurs, "utilisateurs")}</Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </div>
        </Container>
        <Modal show={confirm.show} onHide={() => setConfirm({ ...confirm, show: false })} centered>
          <Modal.Header closeButton><Modal.Title>{confirm.title}</Modal.Title></Modal.Header>
          <Modal.Body>{confirm.message}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirm({ ...confirm, show: false })}>Annuler</Button>
            <Button variant="primary" onClick={confirm.onConfirm}>Confirmer</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CorbeillePage;


